/**
 * Custom hook for tag filtering and pagination
 */
import { useState, useMemo, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const useTagFilters = (tags, sortBy = 'created_desc', colorFilter = 'all') => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBatch, setFilterBatch] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [folderFilter, setFolderFilter] = useState(null); // null = all, 'unorganized' = no folder, or folder_id
  const [currentPage, setCurrentPage] = useState(1);
  const [tagsPerPage, setTagsPerPage] = useState(24);
  const [viewMode, setViewMode] = useState('grid');

  // Get all unique folders
  const getAllFolders = useMemo(() => {
    const folders = new Set();
    tags.forEach(tag => {
      if (tag.batch) {
        folders.add(tag.batch);
      }
    });
    folders.add('uncategorized');
    return Array.from(folders).sort();
  }, [tags]);

  // Get filtered tags based on all filters
  const getFilteredTags = useMemo(() => {
    let filtered = [...tags];
    
    // Check if we have an active search
    const hasSearchQuery = searchQuery.trim().length > 0;

    // Apply search filter - searches ALL tags globally
    if (hasSearchQuery) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(tag =>
        tag.nfc_code?.toLowerCase().includes(query) ||
        tag.description?.toLowerCase().includes(query) ||
        tag.message?.toLowerCase().includes(query) ||
        tag.batch?.toLowerCase().includes(query) ||
        tag.admin_notes?.toLowerCase().includes(query) ||
        tag.folder_id?.toLowerCase().includes(query)
      );
      // When searching, ignore folder filter - search all tags
      // Skip to status filter
    } else {
      // Only apply folder filter when NOT searching
      if (folderFilter !== null) {
        if (folderFilter === 'unorganized') {
          filtered = filtered.filter(tag => !tag.folder_id);
        } else if (typeof folderFilter === 'string' && folderFilter.startsWith('batch:')) {
          // Filter by batch name
          const batchName = folderFilter.substring(6); // Remove 'batch:' prefix
          filtered = filtered.filter(tag => tag.batch === batchName);
        } else {
          // Filter by folder_id
          filtered = filtered.filter(tag => tag.folder_id === folderFilter);
        }
      }
    }

    // Apply batch filter
    if (filterBatch !== 'all') {
      if (filterBatch === 'uncategorized') {
        filtered = filtered.filter(tag => !tag.batch);
      } else {
        filtered = filtered.filter(tag => tag.batch === filterBatch);
      }
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'with-content') {
        filtered = filtered.filter(tag =>
          tag.has_content || tag.message || (tag.media_files && tag.media_files.length > 0)
        );
      } else if (statusFilter === 'empty') {
        filtered = filtered.filter(tag =>
          !tag.has_content && !tag.message && (!tag.media_files || tag.media_files.length === 0)
        );
      } else if (statusFilter === 'expired') {
        const now = new Date();
        filtered = filtered.filter(tag => {
          if (!tag.expires_at) return false;
          try {
            return new Date(tag.expires_at) < now;
          } catch {
            return false;
          }
        });
      } else if (statusFilter === 'recent') {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        filtered = filtered.filter(tag => new Date(tag.created_at) >= sevenDaysAgo);
      }
    }

    // Apply color label filter
    if (colorFilter !== 'all') {
      if (colorFilter === 'none') {
        filtered = filtered.filter(tag => !tag.color_label);
      } else {
        filtered = filtered.filter(tag => tag.color_label === colorFilter);
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'created_desc':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'created_asc':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'name_asc':
          return (a.nfc_code || '').localeCompare(b.nfc_code || '');
        case 'name_desc':
          return (b.nfc_code || '').localeCompare(a.nfc_code || '');
        case 'views_desc':
          return (b.view_count || 0) - (a.view_count || 0);
        case 'views_asc':
          return (a.view_count || 0) - (b.view_count || 0);
        case 'updated_desc':
          return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [tags, searchQuery, filterBatch, statusFilter, folderFilter, sortBy, colorFilter]);

  // Get paginated tags
  const getPaginatedTags = useMemo(() => {
    const startIndex = (currentPage - 1) * tagsPerPage;
    return getFilteredTags.slice(startIndex, startIndex + tagsPerPage);
  }, [getFilteredTags, currentPage, tagsPerPage]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(getFilteredTags.length / tagsPerPage);
  }, [getFilteredTags.length, tagsPerPage]);

  // Get counts for status filters
  const statusCounts = useMemo(() => {
    const withContent = tags.filter(t =>
      t.has_content || t.message || (t.media_files && t.media_files.length > 0)
    ).length;
    const empty = tags.filter(t =>
      !t.has_content && !t.message && (!t.media_files || t.media_files.length === 0)
    ).length;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recent = tags.filter(t => new Date(t.created_at) >= sevenDaysAgo).length;
    
    // Count expired tags
    const now = new Date();
    const expired = tags.filter(t => {
      if (!t.expires_at) return false;
      try {
        return new Date(t.expires_at) < now;
      } catch {
        return false;
      }
    }).length;

    return {
      all: tags.length,
      'with-content': withContent,
      withContent,
      empty,
      expired,
      recent,
    };
  }, [tags]);

  // Get folder counts
  const folderCounts = useMemo(() => {
    const counts = {};
    getAllFolders.forEach(folder => {
      if (folder === 'uncategorized') {
        counts[folder] = tags.filter(t => !t.batch).length;
      } else {
        counts[folder] = tags.filter(t => t.batch === folder).length;
      }
    });
    return counts;
  }, [tags, getAllFolders]);

  // Get tags grouped by folder
  const getTagsByFolder = useMemo(() => {
    const grouped = {};
    getAllFolders.forEach(folder => {
      grouped[folder] = [];
    });

    let filtered = tags;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(tag =>
        tag.nfc_code?.toLowerCase().includes(query) ||
        tag.description?.toLowerCase().includes(query) ||
        tag.message?.toLowerCase().includes(query) ||
        tag.batch?.toLowerCase().includes(query) ||
        tag.admin_notes?.toLowerCase().includes(query)
      );
    }

    filtered.forEach(tag => {
      const folder = tag.batch || 'uncategorized';
      if (!grouped[folder]) grouped[folder] = [];
      grouped[folder].push(tag);
    });

    return grouped;
  }, [tags, searchQuery, getAllFolders]);

  // Get tag thumbnail - prefer thumbnail_url if available, fallback to full image
  const getTagThumbnail = useCallback((tag) => {
    if (tag.media_files && tag.media_files.length > 0) {
      const imageFile = tag.media_files.find(f =>
        f.content_type?.startsWith('image/')
      );
      if (imageFile) {
        // Use thumbnail if available (much faster loading)
        const thumbnailUrl = imageFile.thumbnail_url;
        const fullUrl = imageFile.url;
        
        const url = thumbnailUrl || fullUrl;
        return url.startsWith('http')
          ? url
          : `${BACKEND_URL}${url}`;
      }
    }
    return null;
  }, []);

  // Reset to first page when filters change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  }, [totalPages]);

  const handleItemsPerPageChange = useCallback((count) => {
    setTagsPerPage(count);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((filter) => {
    setStatusFilter(filter);
    setCurrentPage(1);
  }, []);

  const handleBatchFilterChange = useCallback((batch) => {
    setFilterBatch(batch);
    setCurrentPage(1);
  }, []);

  const handleFolderFilterChange = useCallback((folderId) => {
    setFolderFilter(folderId);
    setCurrentPage(1);
  }, []);

  return {
    // State
    searchQuery,
    filterBatch,
    statusFilter,
    folderFilter,
    currentPage,
    tagsPerPage,
    viewMode,
    
    // Computed
    getAllFolders,
    getFilteredTags,
    getPaginatedTags,
    totalPages,
    statusCounts,
    folderCounts,
    getTagsByFolder,
    
    // Utilities
    getTagThumbnail,
    
    // Setters
    setSearchQuery: handleSearchChange,
    setFilterBatch: handleBatchFilterChange,
    setStatusFilter: handleStatusFilterChange,
    setFolderFilter: handleFolderFilterChange,
    setCurrentPage: handlePageChange,
    setTagsPerPage: handleItemsPerPageChange,
    setViewMode,
  };
};

export default useTagFilters;
